import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";

/**
 * Assumptions store — per-workshop register of working assumptions (the
 * "we're proceeding as if X is true" record behind Insight & Knowledge
 * Capture). One of the four workshop-record stores built on
 * {@link createWorkshopRecordStore}; see that factory for the shared
 * persistence posture (IndexedDB by default; the BFF blob plane when
 * enabled). Status moves assumed → validated → refuted, so it uses
 * `update()`.
 */

export type AssumptionStatus = "assumed" | "validated" | "refuted";

export type Assumption = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  workshopId: string;
  /** The assumption itself. */
  statement: string;
  /** Why we believe it / what it rests on. */
  basis: string;
  /** Who owns validating it (display name). */
  owner: string;
  status: AssumptionStatus;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  records, defaulted to `[]` at every read site. */
  tags?: string[];
};

/** Mutable fields a facilitator can edit after an assumption is logged. */
export type AssumptionPatch = Partial<
  Pick<Assumption, "statement" | "basis" | "owner" | "status" | "tags">
>;

const STATUSES: AssumptionStatus[] = ["assumed", "validated", "refuted"];

export const useAssumptions = createWorkshopRecordStore<
  Assumption,
  AssumptionPatch
>({
  idbKey: "xaes-assumptions",
  recordType: "assumptions",
  // Unknown status enums default to "assumed"; see useDecisions for the
  // shared sanitiser rationale.
  sanitiseItem: (a, fallbackWorkshopId) => {
    if (typeof a.id !== "string") return null;
    if (typeof a.statement !== "string") return null;
    const status: AssumptionStatus = STATUSES.includes(
      a.status as AssumptionStatus,
    )
      ? (a.status as AssumptionStatus)
      : "assumed";
    return {
      id: a.id,
      workshopId:
        typeof a.workshopId === "string" ? a.workshopId : fallbackWorkshopId,
      statement: a.statement,
      basis: typeof a.basis === "string" ? a.basis : "",
      owner: typeof a.owner === "string" ? a.owner : "",
      status,
      createdAt: typeof a.createdAt === "number" ? a.createdAt : Date.now(),
      addedBy: typeof a.addedBy === "string" ? a.addedBy : "",
      // P0.1 — preserve free-form tags when present; old blobs round-trip
      // cleanly (field stays absent). Array coerced + filtered to strings.
      tags: Array.isArray(a.tags)
        ? a.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
